import type { FileMeta } from '@jotta/types/FileMeta'

export type { FileMeta } from '@jotta/types/FileMeta'

export type UploadStatus = 'UPLOADING' | 'SUCCESS' | 'ERROR'
export type FileAsObject = Pick<
  File,
  'name' | 'size' | 'type' | 'lastModified'
> & {
  relativePath?: string
}
export interface Upload {
  id: string
  uploadId?: string
  status: UploadStatus
  uploadUrl?: string
  fileName: File['name']
  fileSize: File['size']
  fileType: File['type']
  uploadedBytes: number
  error?: any
}

export function isFileEntry(
  entry: FileSystemEntry | null,
): entry is FileSystemFileEntry {
  return Boolean(entry && entry.isFile)
}

export function isDirectoryEntry(
  entry: FileSystemEntry | null,
): entry is FileSystemDirectoryEntry {
  return Boolean(entry && entry.isDirectory)
}

function readEntriesPromised(
  dirReader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    dirReader.readEntries(resolve, reject)
  })
}

async function readAllEntries(dirReader: FileSystemDirectoryReader) {
  const entries: FileSystemEntry[] = []
  let readEntries: FileSystemEntry[] = []

  do {
    readEntries = (await readEntriesPromised(dirReader)) || []
    entries.push(...readEntries)
  } while (readEntries.length)

  return entries
}

export async function getFilesFromEntries(
  entries: FileSystemEntry[],
  onError: (path: string[]) => void,
) {
  const errorPath: string[] = []
  const p: Promise<FileMeta[][]> = Promise.all(
    entries.map(async entry => {
      if (isFileEntry(entry)) {
        const file = await new Promise<File>((resolve, reject) =>
          entry.file(resolve, reject),
        )
        return [
          {
            file,
            relativePath: entry.fullPath,
          },
        ]
      } else if (isDirectoryEntry(entry)) {
        const dirReader = entry.createReader()
        return readAllEntries(dirReader)
          .then(entries => getFilesFromEntries(entries, onError))
          .catch(() => {
            errorPath.push(entry.fullPath)
            return []
          })
          .finally(() => {
            if (errorPath.length) {
              onError(errorPath)
            }
          })
      } else {
        errorPath.push(entry.fullPath)
        return []
      }
    }),
  )

  return (await p).flat()
}

export async function getFilesFromDataTransferItem(
  item: DataTransferItem,
  onError: (path: string[]) => void,
) {
  const entry = item.webkitGetAsEntry()
  if (entry) {
    return getFilesFromEntries([entry], onError)
  }

  const file = item.getAsFile()
  if (!file) {
    return []
  }

  return [{ file }]
}

export async function getFilesFromDataTransferItemList(
  list: DataTransferItemList,
  onError: (path: string[]) => void,
) {
  const errorPath: string[] = []
  const files = (
    await Promise.all(
      Array.from(list).map(item =>
        getFilesFromDataTransferItem(item, path => errorPath.push(...path)),
      ),
    )
  ).flat()

  if (onError && errorPath.length) {
    onError(errorPath)
  }

  return files
}

export function getFileExtension(path: string): string {
  const idx = path.lastIndexOf('.')
  if (idx === -1) {
    return ''
  } else {
    return path.substring(idx + 1)
  }
}

/**
 * Image extensions the browser can decode natively via `<img>`. Anything
 * outside this set (HEIC, RAW, …) needs a server-transcoded thumbnail
 * before the editor can render it.
 */
export const RENDERABLE_IMAGE_FORMATS = new Set(['jpg', 'jpeg', 'png', 'webp'])

export function isImageRenderable(filename: string | undefined): boolean {
  if (!filename) {
    return false
  }
  const ext = filename.toLowerCase().split('.').pop()
  return Boolean(ext && RENDERABLE_IMAGE_FORMATS.has(ext))
}

/**
 * Pick an image MIME type from a filename's extension. Defaults to JPEG
 * when the extension is missing or unknown.
 */
export function inferImageMimeType(filename: string | undefined): string {
  const ext = filename?.toLowerCase().split('.').pop()
  switch (ext) {
    case 'png':
      return 'image/png'
    case 'webp':
      return 'image/webp'
    case 'jpg':
    case 'jpeg':
    default:
      return 'image/jpeg'
  }
}

export interface EditedFilenameOptions {
  /** Appends a `-YYYYMMDD-HHMMSSmmm` suffix before the extension. */
  withTimestamp?: boolean
}

/**
 * Build a filename for an edited copy of `originalName`. Picks the
 * extension from `mimeType` (png / webp / jpg fallback).
 */
export function buildEditedFilename(
  originalName: string,
  mimeType: string,
  options: EditedFilenameOptions = {},
): string {
  const dotIdx = originalName.lastIndexOf('.')
  const stem = dotIdx > 0 ? originalName.slice(0, dotIdx) : originalName
  const ext =
    mimeType === 'image/png'
      ? 'png'
      : mimeType === 'image/webp'
        ? 'webp'
        : 'jpg'
  const suffix = options.withTimestamp
    ? `-${formatEditedTimestamp(new Date())}`
    : ''
  return `${stem}-edited${suffix}.${ext}`
}

function formatEditedTimestamp(date: Date): string {
  const pad = (value: number, length = 2) =>
    value.toString().padStart(length, '0')
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    '-',
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
    pad(date.getMilliseconds(), 3),
  ].join('')
}
